/**
 * A viewer's map adjustments — layers, colouring, filters — kept in their own browser, per
 * report (RP5). Storage may be missing or refuse (private windows); the view then lasts the visit.
 */
import { useCallback, useState } from 'react';
import type { LayerOptions } from '@/map/layers';

export type StoredMapView = Omit<LayerOptions, 'baseUrl'>;

export const mapViewKey = (id: string) => `forestum.report.${id}.map`;

export function readMapView(id: string): StoredMapView {
  try {
    const raw = window.localStorage.getItem(mapViewKey(id));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as StoredMapView) : {};
  } catch {
    return {};
  }
}

export function writeMapView(id: string, view: StoredMapView): void {
  try {
    window.localStorage.setItem(mapViewKey(id), JSON.stringify(view));
  } catch {
    // Storage unavailable: the view lasts this visit.
  }
}

export function clearMapView(id: string): void {
  try {
    window.localStorage.removeItem(mapViewKey(id));
  } catch {
    // Storage unavailable: nothing was kept.
  }
}

export function useStoredMapView(id: string): [StoredMapView, (view: StoredMapView) => void, () => void] {
  const [stored, setStored] = useState(() => ({ id, view: readMapView(id) }));
  // Another report in the same page: its own view, read during render rather than after a
  // frame showing (and possibly saving) the previous report's.
  if (stored.id !== id) setStored({ id, view: readMapView(id) });
  const set = useCallback(
    (next: StoredMapView) => {
      setStored({ id, view: next });
      writeMapView(id, next);
    },
    [id],
  );
  const reset = useCallback(() => {
    setStored({ id, view: {} });
    clearMapView(id);
  }, [id]);
  return [stored.view, set, reset];
}
